import { createStructureStatementHandler } from '../../createStructureStatementHandler';
import { getScopeName, getViewName } from '../../utils/nameFormat';
export const addFilterScopeStmtInstruction = `
## Defining Filters for Scopes
Filters are crucial for enhancing the user's ability to navigate through large sets of data efficiently. They enable users to narrow down the data displayed in a datagrid based on specific criteria. Filter's query has to use fields from the entity that is connected to the scope. DON'T INCLUDE THE FILTERS FOR NON-EXISTING FIELDS.

### Syntax for Adding a Filter to a Scope
To add a filter to a scope, use the following DSL command structure:
- Syntax: \`create filter scope <view_name>.<scope_name> where <filter_query>;\`
- Purpose: Specifies criteria to narrow down the data displayed in the specified scope.

### Constructing a Filter Query
When constructing a filter query, specify the condition(s) that the data must meet to be included in the datagrid. These conditions can be based on the values of certain fields, the presence of specific data, or the comparison of dates and numerical values.

#### Operators Available for Filter Queries:
- \`=\` (equal to)
- \`!=\` (not equal to)
- \`>\` (greater than)
- \`<\` (less than)
- \`>=\` (greater than or equal to)
- \`<=\` (less than or equal to)

### Filter Query Examples:
1. **Simple Equality:** \`create filter scope ${getViewName({ entityName: 'Employee', viewType: 'dataGrid' })}.${getScopeName({ entityName: 'Employee' })} where status = 'active';\` - Displays only active employees.
2. **Numerical Comparison:** \`create filter scope ${getViewName({ entityName: 'Inventory', viewType: 'dataGrid' })}.${getScopeName({ entityName: 'Product' })} where stock > 20;\` - Shows products with stock levels above 20.
3. **Date Comparison:** \`create filter scope ${getViewName({ entityName: 'Order', viewType: 'dataGrid' })}.${getScopeName({ entityName: 'Order' })} where orderDate >= '2023-01-01';\` - Lists orders placed on or after January 1, 2023.
4. **Combining Conditions:** \`create filter scope ${getViewName({ entityName: 'User', viewType: 'dataGrid' })}.${getScopeName({ entityName: 'User' })} where [age >= 18] and [subscriptionStatus = 'active'];\` - Displays active subscribers who are 18 years old or older.
5. **Nested Relations:** \`create filter scope ${getViewName({ entityName: 'Article', viewType: 'dataGrid' })}.${getScopeName({ entityName: 'Article' })} where author.profile.age > 30;\` - Filters articles written by authors over 30 years old.

### Advanced Filter Features:
- **Dynamic Filtering:** Implement filters that can adapt based on user input, session data, or other contextual information to enhance flexibility and user experience.
- **Complex Conditions:** Combine multiple conditions using logical operators (AND, OR) to create sophisticated filters that can handle complex user requirements.

By following these guidelines, developers can create more intuitive, powerful, and user-friendly filters for datagrids, significantly improving the application's data presentation and user interaction capabilities. Make sure, you are using the correct field names from entity and operators in the filter query to avoid any errors.
`;
export const stmtAddFilterScope = ({ scopeName, viewName, filterQuery }) => {
    return `create filter scope ${viewName}.${scopeName} where ${filterQuery};`;
};
export const addFilterScopeHandler = createStructureStatementHandler({
    type: 'create filter scope',
    parse: line => {
        const match = /^(create filter|filter) scope (?<viewName>\w+)\.(?<scopeName>\w+) where (?<filterQuery>[\w\s=><!@#$%^&*()\-+[\]{}:'",./?|~]+)/.exec(line);
        return match?.groups
            ? {
                type: 'create filter scope',
                scopeName: match.groups.scopeName,
                viewName: match.groups.viewName,
                filterQuery: match.groups.filterQuery,
            }
            : null;
    },
    apply: (stmt, builder) => builder.updateStructure(it => it.addFilterScope({
        scopeName: stmt.scopeName,
        viewName: stmt.viewName,
        filterQuery: stmt.filterQuery,
    })),
});
//# sourceMappingURL=AddFilterScopeStmt.js.map